/**
 * VFX3: photographic Milky Way / nebulae backdrop from NASA SVS "Deep Star Maps
 * 2020" (svs.gsfc.nasa.gov/4851, public domain) — equirectangular EXR.
 *
 * Applied as `scene.background` with EquirectangularReflectionMapping: three
 * renders it as an infinite backdrop that automatically follows the camera and
 * sits behind ALL geometry, so it never needs floating-origin handling and the
 * HYG stars + planets composite in front of it. The background is not part of
 * the bloom input by default, so the Milky Way band won't bloom into a wash.
 */
import * as THREE from 'three';
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader.js';
import { buildSkyboxEnvironment } from './scene.js';

/**
 * Load the equirect EXR and set it as the scene background.
 * @param intensity scales the HDR backdrop so it reads but doesn't overpower the
 *                  HYG catalog stars / lit planets.
 */
export const loadSkybox = async (
  scene: THREE.Scene,
  renderer: THREE.WebGLRenderer,
  url = '/assets/sky/milkyway_4k.exr',
  intensity = 0.6,
): Promise<void> => {
  const loader = new EXRLoader();
  try {
    const tex = await loader.loadAsync(url);
    tex.mapping = THREE.EquirectangularReflectionMapping;
    tex.colorSpace = THREE.LinearSRGBColorSpace;
    scene.background = tex;
    scene.backgroundIntensity = intensity;
    // WAVE 2a — REAL IBL: prefilter the SAME Milky Way EXR into the scene
    // environment, replacing the gradient-dome placeholder createScene() set as
    // the pre-load fallback. Reuses the loaded asset (no new download); the old
    // placeholder PMREM is disposed. environmentIntensity stays 0.35 (scene.ts).
    const previousEnv = scene.environment;
    scene.environment = buildSkyboxEnvironment(renderer, tex);
    previousEnv?.dispose();
  } catch (err) {
    console.warn(`[assets] skybox EXR missing (${url}) — keeping black background`, err);
  }
};
